import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { Message } from '@chatapp-inbox/shared';
import { isApiError, retryMessage } from '../../api';
import {
  conversationKey,
  invalidateConversationLists,
  isReplyWindowClosed,
  messagesKey,
  recordWindowRefusal,
  refusalText,
} from '../conversation';
import { replaceMessageInCache } from './messages-cache';

/** Texto para a pessoa quando o reenvio de uma mensagem do servidor falha. */
export function retryErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    if (error.code === 'not_retryable') return 'Esta mensagem não pode mais ser reenviada.';
    if (error.code === 'not_assigned') {
      return 'Você não é mais o responsável por esta conversa.';
    }
    if (error.code === 'reply_window_closed') return refusalText(error);
    if (error.status === 404) return 'Esta mensagem não está mais disponível.';
    if (error.status === 0 || error.status >= 500) {
      return 'Não foi possível reenviar agora. Tente de novo.';
    }
    return error.message;
  }
  return 'Não foi possível reenviar. Tente de novo.';
}

/**
 * Reenvio de mensagens de saída que o servidor marcou como `error`. A resposta (a mesma mensagem,
 * de novo `pending`) troca a antiga no cache. Controla, por mensagem, o pedido em andamento e o
 * último erro, para o botão não disparar duas vezes e a falha aparecer junto da mensagem. O
 * reenvio devolve se deu certo (a tela leva a pessoa ao fim, onde a mensagem passa a ficar).
 */
export function useRetry(conversationId: string) {
  const queryClient = useQueryClient();
  const [retrying, setRetrying] = useState<ReadonlySet<string>>(() => new Set());
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  // Quando cada mensagem voltou a "enviando" por um reenvio daqui. O `createdAt` do servidor é o da
  // criação (talvez de horas atrás): contar o "Ainda enviando…" dele faria o reenvio recém-clicado
  // já parecer demorado.
  const [retriedAt, setRetriedAt] = useState<Readonly<Record<string, number>>>({});

  const retry = useCallback(
    async (message: Message): Promise<boolean> => {
      setRetrying((current) => new Set(current).add(message.id));
      setErrors((current) => {
        const rest = { ...current };
        delete rest[message.id];
        return rest;
      });
      try {
        const updated = await retryMessage(conversationId, message.id);
        // Uma busca em voo que começou antes do reenvio sobrescreveria a troca abaixo (a mensagem
        // voltaria ao lugar e ao estado de erro antigos).
        await queryClient.cancelQueries({ queryKey: messagesKey(conversationId), exact: true });
        replaceMessageInCache(queryClient, conversationId, updated);
        setRetriedAt((current) => ({ ...current, [message.id]: Date.now() }));
        void invalidateConversationLists(queryClient);
        return true;
      } catch (error) {
        setErrors((current) => ({ ...current, [message.id]: retryErrorMessage(error) }));
        if (isApiError(error)) {
          // A mensagem ou a permissão mudou por baixo: a tela precisa do estado verdadeiro.
          if (error.status === 404 || error.code === 'not_retryable') {
            void queryClient.invalidateQueries({ queryKey: messagesKey(conversationId) });
          }
          if (error.code === 'not_assigned') {
            void queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
          }
          // A janela fechou entre a tela e o reenvio (409): a mensagem continua como estava (com
          // erro), sem texto a devolver. A tela passa a mostrar o aviso de janela fechada.
          if (isReplyWindowClosed(error)) {
            recordWindowRefusal(queryClient, conversationId, {
              message: refusalText(error),
              draftKept: false,
            });
            void queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
            void invalidateConversationLists(queryClient);
          }
        }
        return false;
      } finally {
        setRetrying((current) => {
          const next = new Set(current);
          next.delete(message.id);
          return next;
        });
      }
    },
    [conversationId, queryClient],
  );

  return { retry, retrying, errors, retriedAt };
}
